import type {
  UpdateModulesMutation,
  UpdateModulesMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateModulesTRPCInput {
  ModuleSettings: UpdateModulesMutationVariables['ModuleSettings'];
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateModulesMutation => ({
  insert_organisation_module: {
    __typename: 'organisation_module_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpdateModulesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.organisationModule.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.organisationModule.getByOrgId.queryKey(),
        });
        // COI-889: module grants gate `checkNavigationVisibility`, cached for
        // 60 min with `refetchOnMount: false`.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.permission.pathKey(),
        });
      },
    }),
  });

  return {
    updateModules: async (
      input: UpdateModulesTRPCInput
    ): Promise<UpdateModulesMutation> => {
      const result = await mutation.mutateAsync({
        ModuleSettings: input.ModuleSettings,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
